/**
 * Add Label Configuration
 * Type-specific configuration for adding label inventory
 */

import { supabase } from '../../../../../supabaseClient';

export const addLabelConfig = {
  type: 'labels',
  displayName: 'Label',
  tableName: 'labels',
  inventoryTable: 'label_inventory',
  itemsTable: 'label_inventory_items',
  transactionsTable: 'label_transactions',
  itemIdField: 'label_id',
  
  // Storage buckets
  storageBuckets: {
    invoices: 'labels-transactions-invoices',
    po: 'labels-transactions-po',
    coa: 'labels-transactions-coa',
    msds: 'labels-transactions-msds',
    bol: 'labels-transactions-bol'
  },
  
  // Form fields configuration (same structure as bottles/caps)
  formFields: [
    { name: 'label_id', label: 'Label', type: 'select', required: true, section: 'basic' },
    { name: 'location_id', label: 'Location', type: 'select', required: true, section: 'basic' },
    { name: 'quantity', label: 'Quantity', type: 'number', required: true, min: 1, section: 'basic' },
    { name: 'lot_number', label: 'Lot Number', type: 'text', required: true, section: 'lot' },
    { name: 'batch_number', label: 'Batch Number', type: 'text', required: true, section: 'lot' },
    { name: 'supplier_id', label: 'Vendor', type: 'select', required: false, section: 'vendor' },
    { name: 'po_number', label: 'PO Number', type: 'text', required: false, section: 'vendor' },
    { name: 'po_date', label: 'PO Date', type: 'date', required: false, section: 'vendor' },
    { name: 'invoice_number', label: 'Invoice Number', type: 'text', required: false, section: 'vendor' },
    { name: 'invoice_date', label: 'Invoice Date', type: 'date', required: false, section: 'vendor' },
    { name: 'invoice_document', label: 'Invoice Document', type: 'file', required: false, accept: '.pdf,.jpg,.jpeg,.png', section: 'documents' },
    { name: 'po_document', label: 'PO Document', type: 'file', required: false, accept: '.pdf,.jpg,.jpeg,.png', section: 'documents' },
    { name: 'coa_document', label: 'COA Document', type: 'file', required: false, accept: '.pdf,.jpg,.jpeg,.png', section: 'documents' },
    { name: 'msds_document', label: 'MSDS Document', type: 'file', required: false, accept: '.pdf,.jpg,.jpeg,.png', section: 'documents' },
    { name: 'bol_document', label: 'BOL Document', type: 'file', required: false, accept: '.pdf,.jpg,.jpeg,.png', section: 'documents' },
    { name: 'notes', label: 'Notes', type: 'textarea', required: false, rows: 3, section: 'additional' }
  ],
  
  validationRules: {
    quantity: (value) => {
      const num = parseInt(value);
      if (isNaN(num) || num <= 0) return 'Quantity must be a positive number';
      return null;
    }
  },
  
  submitHandler: async (formData, companyId) => {
    try {
      // CRITICAL: Purge ANY existing records (active OR inactive) with same lot/batch
      // This prevents database triggers from reactivating old deleted records
      const { data: existingItems } = await supabase
        .from('label_inventory_items')
        .select('id, source_transaction_id')
        .eq('company_id', companyId)
        .eq('label_id', formData.label_id)
        .eq('location_id', formData.location_id)
        .eq('lot_number', formData.lot_number)
        .eq('batch_number', formData.batch_number);
      
      if (existingItems && existingItems.length > 0) {
        // Delete source transactions first
        const txIds = existingItems.map(i => i.source_transaction_id).filter(id => id);
        if (txIds.length > 0) {
          await supabase
            .from('label_transactions')
            .delete()
            .in('id', txIds);
        }
        
        // Delete inventory items
        await supabase
          .from('label_inventory_items')
          .delete()
          .eq('company_id', companyId)
          .eq('label_id', formData.label_id)
          .eq('location_id', formData.location_id)
          .eq('lot_number', formData.lot_number)
          .eq('batch_number', formData.batch_number);
      }
      
      // Fetch unit_cost from labels table
      const { data: labelData, error: labelError } = await supabase
        .from('labels')
        .select('unit_cost')
        .eq('id', formData.label_id)
        .single();
      
      if (labelError) throw labelError;
      
      // Upload documents FIRST (before creating transaction)
      const documentPaths = {};
      const bucketMap = {
        invoice: 'labels-transactions-invoices',
        po: 'labels-transactions-po',
        coa: 'labels-transactions-coa',
        msds: 'labels-transactions-msds',
        bol: 'labels-transactions-bol'
      };
      
      for (const docType of ['invoice', 'po', 'coa', 'msds', 'bol']) {
        const file = formData[`${docType}_document`];
        if (file) {
          const fileName = `${companyId}/${Date.now()}_${file.name}`;
          const bucketName = bucketMap[docType];
          const { error: uploadError } = await supabase.storage.from(bucketName).upload(fileName, file);
          if (uploadError) throw uploadError;
          documentPaths[`${docType}_file_path`] = fileName;
        }
      }
      
      // Create transaction record with document paths
      const transactionData = {
        company_id: companyId,
        label_id: formData.label_id,
        location_id: formData.location_id,
        transaction_type: 'inventory_creation',
        quantity: parseInt(formData.quantity),
        lot_number: formData.lot_number,
        batch_number: formData.batch_number,
        supplier_id: formData.supplier_id || null,
        po_number: formData.po_number || null,
        po_date: formData.po_date || null,
        invoice_number: formData.invoice_number || null,
        invoice_date: formData.invoice_date || null,
        unit_cost: labelData?.unit_cost || null,
        notes: formData.notes || null,
        ...documentPaths,
        created_at: new Date().toISOString()
      };
      
      const { data: transaction, error: txError } = await supabase
        .from('label_transactions')
        .insert(transactionData)
        .select()
        .single();
      
      if (txError) throw txError;
      
      // Create inventory item (NO document paths here!)
      const inventoryItemData = {
        company_id: companyId,
        label_id: formData.label_id,
        location_id: formData.location_id,
        current_quantity: parseInt(formData.quantity),
        original_quantity: parseInt(formData.quantity),
        lot_number: formData.lot_number,
        batch_number: formData.batch_number,
        source_transaction_id: transaction.id,
        is_active: true,
        created_at: new Date().toISOString()
      };
      
      const { data: inventoryItem, error: itemError } = await supabase
        .from('label_inventory_items')
        .insert(inventoryItemData)
        .select()
        .single();
      
      if (itemError) throw itemError;
      
      await supabase.rpc('recalculate_label_aggregate_inventory', {
        p_company_id: companyId,
        p_label_id: formData.label_id,
        p_location_id: formData.location_id
      });
      
      return { success: true, transaction, inventoryItem };
    } catch (error) {
      console.error('Error adding label inventory:', error);
      throw error;
    }
  }
};

export default addLabelConfig;
